import { useMemo } from 'react';

type Props = {
  playerSlug?: string;
  teamSlug?: string;
  primary?: boolean;
  auction?: boolean;
  secondary?: boolean;
  primaryOffer?: boolean;
  rarity?: string;
  countrySlug?: string;
  nationalTeamSlug?: string;
  userId?: string;
  leagueSlug?: string;
  bundled?: boolean;
  onSale?: boolean;
  extraFilters?: string;
};

export const useDefaultFilters = ({
  playerSlug,
  teamSlug,
  primary,
  auction,
  secondary,
  onSale,
  primaryOffer,
  rarity,
  countrySlug,
  nationalTeamSlug,
  userId,
  leagueSlug,
  bundled,
  extraFilters,
}: Props): string[] => {
  // A record in the CardsOnSale index keeps the `sale` it was indexed with after the card stops
  // selling, and only `on_sale` is recomputed. A `sale.*` filter on its own therefore still matches
  // sales that are over, and the Ending Soon sort ranks those ahead of every live sale. A caller
  // asking for a sale type is asking for cards on sale, so scope it to `on_sale` as well. Callers
  // that pass no sale type search the whole card index and must not be scoped.
  const onSaleScoped =
    !!primary || !!auction || !!primaryOffer || !!secondary || !!onSale;

  const filters = useMemo(
    () =>
      [
        playerSlug && `player.slug:${playerSlug}`,
        teamSlug && `team.slug:${teamSlug}`,
        primary && 'sale.primary:true',
        auction && ['sale.primary:true', 'sale.type:EnglishAuction'],
        primaryOffer && ['sale.primary:true', 'sale.type:PrimaryOffer'],
        secondary && 'sale.primary:false',
        onSaleScoped && 'on_sale:true',
        rarity && `rarity:${rarity}`,
        countrySlug && `country.code:${countrySlug}`,
        nationalTeamSlug && `active_national_team.slug:${nationalTeamSlug}`,
        userId && `user.id:${userId}`,
        leagueSlug && `active_league.slug:${leagueSlug}`,
        bundled !== undefined && `sale.bundled:${bundled}`,
        extraFilters,
      ]
        .flat()
        .filter(Boolean),
    [
      playerSlug,
      teamSlug,
      primary,
      auction,
      primaryOffer,
      secondary,
      onSaleScoped,
      rarity,
      countrySlug,
      nationalTeamSlug,
      userId,
      leagueSlug,
      bundled,
      extraFilters,
    ]
  );

  return filters;
};
